<template>
  <div>
    <q-card
      class="column cursor-pointer"
      :class="{
        [`bg-${backgroundColor}`]: backgroundColor !== 'custom',
        [`text-${textColor}`]: textColor !== 'custom',
        'bg-transparent': backgroundColor === 'default' || !backgroundColor,
        [defaultTextColor]: !textColor || textColor === 'default',
        'q-pa-md': !dense,
        'q-pa-xs': dense
      }"
      :style="{
        'background-color': backgroundColor === 'custom' ? backgroundColorHex : '',
        'color': textColor === 'custom' ? textColorHex : '',
        'border-radius': `${borderRadius}px`,
        'margin-top': showImage ? '30px' : '0px',
        'min-height': showImage ? undefined : '74px',
      }"
      flat
      bordered
      :dark="(!defaultTextColor || textColor === 'default') && defaultTextColor === 'text-white'"
    >
      <q-card-section
        class="position-relative"
      >
        <q-badge
          v-if="minimumAmount > 1"
          color="primary"
          :text-color="$getColor(currentSite, 'primary')"
          floating
          style="width: 80px; height: 80px; border-radius: 40px;"
          class="text-body1 text-bold items-center justify-center"
          rounded
        >
          {{ $t('common.min') }} {{ minimumAmount }}
        </q-badge>
        <img
          v-if="showImage"
          class="full-width"
          :style="{ 'border-radius': `${borderRadius + 8}px` }"
          :src="voucher.imageUrl || currentSite.voucherSaleImageUrl"
        >
      </q-card-section>

      <q-card-section class="column">
        <div
          class="text-h6 text-weight-bold"
          :class="`text-${(textAlign || 'center').toLowerCase()}`"
        >
          {{ name || voucher.name }}
        </div>
        <div
          class="text-h5"
          :class="`text-${(textAlign || 'center').toLowerCase()}`"
        >
          <i18n-n
            :value="voucher.price"
            format="currency"
            scope="global"
          />
        </div>
        <div
          v-if="voucher.description"
          class="text-body1 q-mt-md"
          :class="`text-${(textAlign || 'center').toLowerCase()}`"
          style="white-space: pre-wrap;"
          v-html="voucher.description"
        />
      </q-card-section>
      <q-card-actions class="position-relative">
        <q-btn
          v-if="!count || count === 0"
          class="full-width"
          :label="$t('actions.add')"
          padding="sm md"
          size="md"
          color="primary"
          :text-color="$getColor(currentSite, 'primary')"
          style="background-color: rgba(0, 0, 0, 0.05)"
          :style="{ 'border-radius': `${borderRadius}px` }"
          @click.stop="increaseCount"
        />
        <q-input
          v-else
          v-model.number="selectedCount"
          class="full-width"
          input-class="text-center"
          dense
          filled
          :min="minimumAmount || 1"
          :max="maxCount"
          :style="{ 'border-radius': `${borderRadius}px` }"
          @update:model-value="handleInput"
        >
          <template #prepend>
            <q-btn
              :style="{ 'border-radius': `${borderRadius}px` }"
              dense
              flat
              icon="fal fa-minus"
              :disabled="selectedCount === 0"
              @click.stop="decreaseCount"
            />
          </template>
          <template #append>
            <q-btn
              :style="{ 'border-radius': `${borderRadius}px` }"
              dense
              flat
              icon="fal fa-plus"
              :disabled="selectedCount >= maxCount"
              @click.stop="increaseCount"
            />
          </template>
        </q-input>
      </q-card-actions>
    </q-card>
  </div>
</template>

<script>
import { TEXT_POSITION_TYPES } from 'src/models/page/Section'
import VoucherPackage from 'src/models/voucher/VoucherPackage'
import VoucherType from 'src/models/voucher/VoucherType'
export default {
  name: 'VoucherCard',
  configuration: ['currentSite', 'currentCircuit', 'itemVariations'],
  props: {
    voucherType: {
      type: VoucherType,
      required: false,
      default: null
    },
    voucherPackage: {
      type: VoucherPackage,
      required: false,
      default: null
    },
    name: {
      type: String,
      required: false,
      default: ''
    },
    minimumAmount: {
      type: Number,
      required: false,
      default: 1
    },
    count: {
      type: Number,
      required: false,
      default: 0
    },
    backgroundColor: {
      type: String,
      required: false,
      default: 'default'
    },
    textColor: {
      type: String,
      required: false,
      default: 'default'
    },
    backgroundColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    textColorHex: {
      type: String,
      required: false,
      default: '#000000'
    },
    showActionButtons: {
      type: Boolean,
      required: false,
      default: true
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    showImage: {
      type: Boolean,
      required: false,
      default: true
    },
    textAlign: {
      type: String,
      required: false,
      default: TEXT_POSITION_TYPES.CENTER
    },
    defaultTextColor: {
      type: String,
      required: false,
      default: null
    },
    dense: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['touch-repeat', 'remove', 'add'],
  data () {
    return {
      selectedCount: this.count
    }
  },
  computed: {
    voucher () {
      return this.voucherPackage || this.voucherType
    },
    align () {
      const alignOption = this.textAlign
      switch (alignOption) {
        case TEXT_POSITION_TYPES.LEFT:
          return 'left'
        case TEXT_POSITION_TYPES.RIGHT:
          return 'right'
        default:
          return 'center'
      }
    },
    maxCount () {
      return Math.max(this.minimumAmount * 2, 10)
    }
  },
  methods: {
    handleInput (val) {
      let parsed = parseInt(this.selectedCount, 10)

      if (isNaN(parsed) || parsed < 0) {
        parsed = 0
      } else if (parsed < this.minimumAmount) {
        parsed = this.minimumAmount
      } else if (parsed > this.maxCount) {
        parsed = this.maxCount
      }

      // Ensure selectedCount is stored as a number
      if (parsed !== this.selectedCount) {
        this.selectedCount = parsed
      }

      if (parsed > this.count) {
        this.$emit('add', this.selectedCount)
      } else if (parsed < this.count) {
        this.$emit('remove', this.selectedCount)
      }
    },
    increaseCount () {
      if (this.selectedCount < this.minimumAmount) {
        this.selectedCount = this.minimumAmount
      } else {
        this.selectedCount++
      }
      this.handleInput()
    },
    decreaseCount () {
      if (this.selectedCount > this.minimumAmount || 0) {
        this.selectedCount--
        this.handleInput()
      } else {
        this.selectedCount = 0
        this.$emit('remove', this.selectedCount)
      }
    }
  }
}

</script>
